import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ACCESS_TOKEN_KEY } from "../base/private/axiosPrivateClient";

export interface AssignmentListItem {
  id: number;
  assignment_id: number;
  assignment_name: string | null;
  class_id: number | null;
  class_name: string | null;
  subject_id: number | null;
  subject_name: string | null;
  grade: number | string | null;
  max_points: number | null;
  total_students: number | null;
  total_attempts: number | null;
  completed: number | null;
  in_progress: number | null;
  updated_at: string | null;
}

export interface AssignmentReportSummary {
  trust_count?: number | null;
  trust_avg?: number | null;
  trust_median?: number | null;
  trust_q1?: number | null;
  trust_q3?: number | null;
  trust_min?: number | null;
  trust_max?: number | null;
  trust_stddev?: number | null;
  avg_credibility?: number | null;
  min_credibility?: number | null;
  total_warnings?: number | null;
  students_with_violations?: number | null;
  students_with_violations_pct?: number | null;
  median_credibility?: number | null;
  stddev_credibility?: number | null;
  total_students?: number | null;
  total_attempts?: number | null;
  completed?: number | null;
  in_progress?: number | null;
  completion_rate_pct?: number | null;
  passed_count?: number | null;
  failed_count?: number | null;
  avg_score_pct?: number | null;
  max_score_pct?: number | null;
  min_score_pct?: number | null;
  stddev_score_pct?: number | null;
  avg_result_pct?: number | null;
  avg_time_min?: number | null;
  max_time_min?: number | null;
  min_time_min?: number | null;
  avg_video_min?: number | null;
  risk_low?: number | null;
  risk_medium?: number | null;
  risk_high?: number | null;
  risk_critical?: number | null;
  [key: string]: unknown;
}

export interface AssignmentReportCluster {
  [key: string]: unknown;
}

export interface AssignmentReport {
  summary: AssignmentReportSummary;
  clusters?: AssignmentReportCluster[];
  actions?: AggregateActions | null;
  generated_at?: string | null;
  max_points?: number | null;
  [key: string]: unknown;
}

export interface AggregateReportItem {
  assignment_id: number;
  assignment_name?: string | null;
  class_id?: number | null;
  class_name?: string | null;
  subject_id?: number | null;
  subject_name?: string | null;
  max_points?: number | null;
  generated_at?: string | null;
  summary?: AssignmentReportSummary;
  [key: string]: unknown;
}

export interface ActionsByType {
  action: string;
  count: number;
}

export interface ActionsByStatus {
  status: string;
  count: number;
}

export interface AggregateActions {
  total: number | null;
  by_type: ActionsByType[];
  by_status: ActionsByStatus[];
}

export interface AggregateReport {
  assignment_ids?: number[];
  total: number;
  summary: AssignmentReportSummary;
  actions?: AggregateActions | null;
  items: AggregateReportItem[];
  max_points?: number | null;
  [key: string]: unknown;
}

export interface AggregateReportRequest {
  assignment_ids: number[];
}

export interface ActionsStatsQuartiles {
  min: number | null;
  q1: number | null;
  median: number | null;
  q3: number | null;
  max: number | null;
}

export interface ActionsStatsSeverity {
  low: number | null;
  medium: number | null;
  high: number | null;
  critical: number | null;
}

export interface ActionsStatsBucket {
  [key: string]: unknown;
}

export interface ActionsStatsActionType {
  [key: string]: unknown;
}

export interface ActionsStatsReport {
  total_warnings: number | null;
  students_with_violations: number | null;
  violations: ActionsStatsQuartiles;
  points: ActionsStatsQuartiles;
  severity: ActionsStatsSeverity;
  buckets: ActionsStatsBucket[];
  action_types: ActionsStatsActionType[];
  [key: string]: unknown;
}

export interface GetAssignmentsParams {
  class_id?: number;
  school_id?: number | null;
  q?: string;
  updated_from?: string;
  updated_to?: string;
  limit?: number;
  offset?: number;
}

const analyticsBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.NEXT_PUBLIC_API_RATING_URL}analytics/`,
  prepareHeaders: (headers) => {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem(ACCESS_TOKEN_KEY)
        : null;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: analyticsBaseQuery,
  endpoints: (builder) => ({
    getAnalyticsAssignments: builder.query<
      AssignmentListItem[],
      GetAssignmentsParams | void
    >({
      query: (args) => {
        const {
          limit = 50,
          offset = 0,
          class_id,
          school_id,
          q,
          updated_from,
          updated_to,
        } = args ?? {};
        return {
          url: "assignments/",
          method: "GET",
          params: {
            limit,
            offset,
            ...(class_id ? { class_id } : {}),
            ...(school_id != null ? { school_id } : {}),
            ...(q ? { q } : {}),
            ...(updated_from ? { updated_from } : {}),
            ...(updated_to ? { updated_to } : {}),
          },
        };
      },
      transformResponse: (response: unknown): AssignmentListItem[] => {
        const res = response as
          | AssignmentListItem[]
          | {
              items?: AssignmentListItem[];
              data?: AssignmentListItem[];
              entities?:
                | AssignmentListItem[]
                | { data?: AssignmentListItem[] };
            }
          | null;
        if (!res) return [];
        if (Array.isArray(res)) return res;
        if (Array.isArray(res.items)) return res.items;
        if (Array.isArray(res.data)) return res.data;
        if (Array.isArray(res.entities)) return res.entities;
        if (res.entities && Array.isArray(res.entities.data))
          return res.entities.data;
        return [];
      },
    }),

    getAnalyticsAssignmentReport: builder.query<AssignmentReport, number>({
      query: (assignmentId) => ({
        url: `assignments/${assignmentId}`,
        method: "GET",
      }),
    }),

    getAnalyticsAggregateReport: builder.query<
      AggregateReport,
      AggregateReportRequest
    >({
      query: (body) => ({
        url: "assignments/aggregate",
        method: "POST",
        body,
      }),
    }),

    getAnalyticsActionsStats: builder.query<ActionsStatsReport, number>({
      query: (assignmentId) => ({
        url: `assignments/${assignmentId}/actions-stats`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAnalyticsAssignmentsQuery,
  useGetAnalyticsAssignmentReportQuery,
  useGetAnalyticsAggregateReportQuery,
  useGetAnalyticsActionsStatsQuery,
} = analyticsApi;
